"use client";
import { useRef, useState } from "react";
import Link from "next/link";
import { ArrowLeft, ArrowRight, Search, FileCheck2, RefreshCw } from "lucide-react";
import { Banner, Button, Desk, Fact, Facts, PageHeader, StatusTag } from ".";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import {
  BATCH_LABEL,
  BATCH_TONE,
  FIELD_LABEL,
  OUTCOME_LABEL,
  OUTCOME_TONE,
  issueText,
  type Report
} from "./import-model";
import { ImportSteps } from "./import-editors";
import { formatTime } from "../../lib/api";
export function ImportReport({
  report,
  onBack,
  onAction,
  reload,
  message
}: {
  report: Report;
  onBack: () => void;
  onAction: (action: "commit" | "discard") => void;
  reload: () => void;
  message: string;
}) {
  const b = report.batch,
    [status, setStatus] = useState(""),
    [word, setWord] = useState(""),
    [q, setQ] = useState(""),
    [page, setPage] = useState(0),
    [selected, setSelected] = useState<number | null>(null);
  const heading = useRef<HTMLHeadingElement>(null),
    focus = useRef<HTMLElement | null>(null),
    row = report.items.find((r) => r.lineNumber === selected);
  const outcomeLabel = (outcome: string) =>
    b.status === "DISCARDED" && outcome === "READY_NEW"
      ? "预检可新建"
      : b.status === "DISCARDED" && outcome === "READY_UPDATE"
        ? "预检可补录"
        : (OUTCOME_LABEL[outcome] ?? "待核对");
  const filtered = report.items.filter(
    (r) =>
      (!status || (status === "ISSUES" ? r.issues.length > 0 : r.outcome === status)) &&
      (!q ||
        `${r.lineNumber} ${r.mappedValues["person.name"] ?? ""} ${r.issues.map(issueText).join(" ")}`.includes(
          q
        ))
  );
  const rows = filtered.slice(page * 20, (page + 1) * 20),
    ready = report.items.filter(
      (r) => r.outcome === "READY_NEW" || r.outcome === "READY_UPDATE"
    ).length,
    applied = report.items.filter((r) => r.outcome === "APPLIED").length;
  return (
    <Desk wide>
      <Button variant="ghost" className="record-back" onClick={onBack}>
        <ArrowLeft size={16} />
        返回历史批次
      </Button>
      <PageHeader
        eyebrow="会员数据导入 / 对账报告"
        title={b.sourceRef}
        description={`${b.mappingName} · 预检于 ${formatTime(b.recordedAt)}`}
        actions={
          <Button variant="outline" onClick={reload}>
            <RefreshCw size={15} />
            刷新报告
          </Button>
        }
      />
      {message && <Banner tone="success">{message}</Banner>}
      <p className="record-muted">搁置 {(b as typeof b & { heldCount?: number }).heldCount ?? 0} 行，继续留在批次中。</p>
      <ImportSteps step={b.status === "COMMITTED" ? 4 : 2} />
      <div className="import-report-summary">
        <div>
          <span>批次状态</span>
          <StatusTag tone={BATCH_TONE[b.status]}>{BATCH_LABEL[b.status]}</StatusTag>
        </div>
        <div>
          <span>源文件数据行</span>
          <strong>{b.rowCount}</strong>
        </div>
        <div>
          <span>{b.status === "COMMITTED" ? "已入库行" : "通过预检行"}</span>
          <strong>{b.status === "COMMITTED" ? applied : ready}</strong>
        </div>
        <div>
          <span>{b.status === "COMMITTED" ? "未完整入库行" : "需处理行"}</span>
          <strong>
            {b.status === "COMMITTED"
              ? report.items.length - applied
              : report.items.filter((r) => r.issues.length > 0).length}
          </strong>
        </div>
      </div>
      {b.status === "STAGED" ? (
        <p className="record-muted">预检尚未更新会员档案。核对每行来源与处理结果后，再决定入库。</p>
      ) : b.status === "DISCARDED" ? (
        <Banner tone="warning">
          本批已作废{b.discardedAt ? `（${formatTime(b.discardedAt)}）` : ""}。{b.discardedReason}
        </Banner>
      ) : (
        <p className="record-muted">
          入库办理已结束{b.committedAt ? `（${formatTime(b.committedAt)}）` : ""}
          。未完整处理的行保留原因，请核对档案后再处理源文件。
        </p>
      )}
      {b.reason && <p className="import-source">导入理由：{b.reason}</p>}
      <div className="access-filters" aria-label="对账行筛选">
        {[
          ["", "全部"],
          ["ISSUES", "只看问题"],
          ...(b.status === "COMMITTED"
            ? [["APPLIED", "已入库"]]
            : [
                ["READY_NEW", "将新建"],
                ["READY_UPDATE", "将补录"]
              ]),
          ["CONFLICT", "需人工处理"],
          ["INVALID", "需改源文件"]
        ].map(([value, label]) => (
          <button
            key={value}
            aria-pressed={status === value}
            onClick={() => {
              setStatus(value);
              setPage(0);
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="record-toolbar">
        <form
          className="record-search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(word.trim());
            setPage(0);
          }}
        >
          <Search size={17} />
          <input
            aria-label="搜索源文件行号、姓名或问题"
            placeholder="行号、姓名或问题"
            value={word}
            onChange={(e) => setWord(e.target.value)}
          />
          <Button type="submit" variant="outline">
            查找
          </Button>
        </form>
      </div>
      <div className="record-list-meta">
        <span>
          匹配 {filtered.length} 行 · 当前页 {rows.length} 行
        </span>
        <span>行号对应源文件中的起始行</span>
      </div>
      {rows.length ? (
        <div className="record-table-scroll">
          <table className="record-table import-report-table">
            <caption className="sr-only">逐行预检与入库结果</caption>
            <thead>
              <tr>
                <th>源文件行</th>
                <th>姓名</th>
                <th>处理结果</th>
                <th>问题与下一步</th>
                <th>裁决</th>
                <th>
                  <span className="sr-only">核对</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.lineNumber}>
                  <td>第 {r.lineNumber} 行</td>
                  <td>
                    <button
                      className="import-person"
                      onClick={() => {
                        focus.current = document.activeElement as HTMLElement;
                        setSelected(r.lineNumber);
                      }}
                    >
                      {r.mappedValues["person.name"] ?? "未读取到姓名"}
                    </button>
                  </td>
                  <td>
                    <StatusTag tone={OUTCOME_TONE[r.outcome] ?? "neutral"}>
                      {(r as typeof r & { held?: boolean }).held ? '已搁置' : outcomeLabel(r.outcome)}
                    </StatusTag>
                  </td>
                  <td className="import-issue-cell">
                    {r.issues.length
                      ? r.issues.map((issue, i) => <p key={i}>{issueText(issue)}</p>)
                      : r.outcome === "READY_UPDATE"
                        ? "核对既有档案，只补录允许导入的会籍事实。"
                        : r.outcome === "APPLIED"
                          ? "查看本行来源与入库记录。"
                          : "核对原始值与字段对应。"}
                  </td>
                  <td>{(r.outcome === 'CONFLICT' || r.outcome === 'READY_UPDATE' || (r as typeof r & { candidateCount?: number }).candidateCount) ? <Link className="record-link" href={`/member-imports/adjudication?batch=${encodeURIComponent(b.id)}`}>核对裁决</Link> : '无需裁决'}</td>
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`核对第 ${r.lineNumber} 行`}
                      onClick={() => {
                        focus.current = document.activeElement as HTMLElement;
                        setSelected(r.lineNumber);
                      }}
                    >
                      <ArrowRight size={15} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="workspace-empty">
          <FileCheck2 />
          <strong>没有匹配的对账行</strong>
          <p>调整筛选条件或查看全部行。</p>
        </div>
      )}
      <div className="record-pagination">
        <span>第 {page + 1} 页</span>
        <Button variant="outline" disabled={!page} onClick={() => setPage(page - 1)}>
          上一页
        </Button>
        <Button
          variant="outline"
          disabled={(page + 1) * 20 >= filtered.length}
          onClick={() => setPage(page + 1)}
        >
          下一页
        </Button>
      </div>
      {b.status === "STAGED" && (
        <div className="import-report-footer">
          <span>可入库 {ready} 行 · 其他行继续留存</span>
          <div className="workspace-actions">
            <Button variant="outline" onClick={() => onAction("discard")}>
              作废批次
            </Button>
            <Button disabled={!ready} onClick={() => onAction("commit")}>
              核对入库范围
              <ArrowRight size={15} />
            </Button>
          </div>
        </div>
      )}
      <h2 className="sr-only" tabIndex={-1} ref={heading}>
        批次对账报告
      </h2>
      <Sheet
        open={!!row}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <SheetContent
          className="record-sheet import-row-sheet"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            (focus.current?.isConnected ? focus.current : heading.current)?.focus();
          }}
        >
          <SheetHeader>
            <p className="record-eyebrow">源文件与导入字段</p>
            <SheetTitle>
              第 {row?.lineNumber} 行 · {row?.mappedValues["person.name"] ?? "逐行核对"}
            </SheetTitle>
            <SheetDescription>保留原始值，并核对预检后的读入结果。</SheetDescription>
          </SheetHeader>
          {row && (
            <div className="record-sheet-body import-row-reading">
              <section className="record-section">
                <StatusTag tone={OUTCOME_TONE[row.outcome] ?? "neutral"}>
                  {outcomeLabel(row.outcome)}
                </StatusTag>
                {row.issues.map((issue, i) => (
                  <div className="import-issue" key={i}>
                    {issueText(issue)}
                  </div>
                ))}
                {row.issues.some((i) => i.code.startsWith("APPLY_FAILED_")) && (
                  <p>本行可能已有部分字段写入，请先核对既有档案，再决定后续处理。</p>
                )}
                {row.appliedAt && <p>本行入库时间：{formatTime(row.appliedAt)}</p>}
                {row.matchedPersonId && (
                  <Link
                    className="record-link"
                    href={`/members?record=${encodeURIComponent(row.matchedPersonId)}`}
                  >
                    核对既有档案
                    <ArrowRight size={14} />
                  </Link>
                )}
              </section>
              <section className="record-section">
                <h2>逐字段核对</h2>
                <div className="record-table-scroll">
                  <table className="record-table import-compare">
                    <thead>
                      <tr>
                        <th>文件表头</th>
                        <th>原始值</th>
                        <th>导入字段</th>
                        <th>预检读入值</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(row.rawValues).map(([column, raw]) => {
                        const field = b.mappingColumns?.[column],
                          value = field ? row.mappedValues[field] : undefined,
                          problem = row.issues.some((i) => i.column === column),
                          isLookup =
                            field === "membership.organization" ||
                            [
                              "membership.sectorClassification",
                              "person.genderCode",
                              "person.ethnicityCode",
                              "person.politicalStatusCode"
                            ].includes(field ?? "");
                        return (
                          <tr key={column}>
                            <td>{column}</td>
                            <td>{raw || "空值"}</td>
                            <td>{field ? (FIELD_LABEL[field] ?? "未识别字段") : "未对应"}</td>
                            <td>
                              {field
                                ? value
                                  ? isLookup && !problem
                                    ? `${raw}（已匹配）`
                                    : value
                                  : "未读入"
                                : "不导入"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
              <section className="record-section">
                <h2>批次来源</h2>
                <Facts>
                  <Fact label="来源依据">{b.sourceRef}</Fact>
                  <Fact label="对应规则">{b.mappingName}</Fact>
                </Facts>
              </section>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </Desk>
  );
}
